import { useState, type ReactNode, useEffect } from "react";
import { type PageContent } from "../content/pages";

type HeaderProps = {
  pageContent: PageContent;
  heroContent?: ReactNode;
};

const Header = ({ pageContent, heroContent }: HeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { hero } = pageContent;

  // Prevent body scrolling when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    // Remove class when component unmounts
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isMenuOpen]);

  return (
    <header>
      <div 
        className="relative bg-cover bg-center bg-no-repeat before:content-[''] before:absolute before:inset-0 before:bg-gradient-to-b before:from-black/60 before:to-black/60"
        style={{ backgroundImage: `url(${hero?.backgroundImage})` }}
      >
        {/* Navbar */}
        <div className="relative w-full h-[80px] shrink-0 z-20">
          <div className="flex h-[80px] flex-col justify-center items-center flex-nowrap absolute top-0 left-0 right-0">
            <div className="flex p-4 md:px-8 justify-between items-center self-stretch shrink-0 flex-nowrap relative w-full">
              {/* Logo */}
              <div className="flex gap-6 items-center shrink-0 flex-nowrap relative">
                <div className="flex gap-10 items-center shrink-0 flex-nowrap relative">
                  <a 
                    href="https://peatix.com/us/" 
                    className="flex items-start shrink-0 flex-nowrap relative"
                  >
                    <img 
                      src="/host/images/peatix-logo-white.png" 
                      alt="Peatix Logo" 
                      className="w-[142px] h-[32px] shrink-0 relative" 
                    />
                  </a>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center gap-4">
                {/* Hamburger menu button */}
                <div className="relative z-50">
                  <button 
                    className={`
                      md:hidden p-2 rounded-lg
                      ${isMenuOpen ? 'bg-white text-gray-600' : 'text-white'}
                    `}
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    aria-label="Toggle menu"
                  >
                    <svg 
                      className="w-6 h-6" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      {isMenuOpen ? (
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M6 18L18 6M6 6l12 12" 
                        />
                      ) : (
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M4 6h16M4 12h16M4 18h16" 
                        />
                      )}
                    </svg>
                  </button>
                </div>

                {/* Desktop menu */}
                <div className="hidden md:flex gap-1 items-center shrink-0 flex-nowrap relative z-30">
                  <div className="flex px-4 py-2.5 gap-2 justify-center items-center shrink-0 flex-nowrap rounded-lg hover:bg-white/10 transition-colors relative overflow-hidden">
                    <a
                      href="https://peatix.com/us/visiting-japan"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="no-underline"
                    >
                      <span className="h-6 shrink-0 basis-auto text-base font-semibold leading-6 text-white relative text-left whitespace-nowrap">
                        Visiting Japan
                      </span>
                    </a>
                  </div>
                  <div className="flex px-4 py-2.5 gap-2 justify-center items-center shrink-0 flex-nowrap rounded-lg hover:bg-white/10 transition-colors relative overflow-hidden">
                    <a 
                      href="https://peatix.com/us/signin"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="no-underline"
                    >
                      <span className="h-6 shrink-0 basis-auto text-base font-semibold leading-6 text-white relative text-left whitespace-nowrap">
                        Sign in
                      </span>
                    </a>
                  </div>
                  <div className="flex px-4 py-2.5 gap-2 justify-center items-center shrink-0 flex-nowrap rounded-lg hover:bg-white/10 transition-colors relative overflow-hidden">
                    <a 
                      href="https://peatix.com/us/signup"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="no-underline"
                    >
                      <span className="h-6 shrink-0 basis-auto text-base font-semibold leading-6 text-white relative text-left whitespace-nowrap">
                        Register
                      </span>
                    </a>
                  </div>
                  <a 
                    href="https://peatix.com/us/event/create" 
                    className="flex px-4 py-2.5 gap-2 justify-center items-center shrink-0 flex-nowrap bg-white hover:bg-gray-50 transition-colors rounded-lg border border-gray-300 relative overflow-hidden shadow-sm pointer"
                  >
                    <span className="h-6 shrink-0 basis-auto text-base font-semibold leading-6 text-gray-700 relative text-left whitespace-nowrap">
                      Create event
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu overlay */}
        <div 
          className={`
            fixed inset-0 bg-white transition-transform duration-300 ease-in-out md:hidden
            ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}
            z-[1000]
          `}
        >
          {/* Mobile menu header */}
          <div className="h-[80px] px-4 flex items-center justify-between">
            <a 
              href="https://peatix.com/us/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-start shrink-0 flex-nowrap relative"
            >
              <img 
                src="/host/images/peatix-logo.svg"
                alt="Peatix Logo" 
                className="w-[142px] h-[32px] shrink-0 relative" 
              />
            </a>
            <button 
              className="p-2 text-gray-600 rounded-lg"
              onClick={() => setIsMenuOpen(false)}
              aria-label="Close menu"
            >
              <svg 
                className="w-6 h-6" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M6 18L18 6M6 6l12 12" 
                />
              </svg>
            </button>
          </div>

          {/* Mobile menu content */}
          <div className="py-6">
            <div className="flex flex-col">
              <a 
                href="https://peatix.com/us/visiting-japan" 
                className="py-3 px-4 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-base font-semibold"
              >
                Visiting Japan
              </a>
              <a 
                href="https://peatix.com/us/signin" 
                className="py-3 px-4 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-base font-semibold"
              >
                Sign in
              </a>
              <a 
                href="https://peatix.com/us/signup" 
                className="py-3 px-4 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-base font-semibold"
              >
                Register
              </a>
              <a 
                href="https://peatix.com/us/event/create" 
                className="py-3 px-4 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-base font-semibold"
              >
                Create event
              </a>
            </div>
          </div>
        </div>

        {/* Hero Content */}
        {heroContent || (
          <div className="container mx-auto px-4 py-20 text-white relative z-10 flex flex-col items-center gap-6">
            <div className="flex w-full max-w-[1280px] md:px-8 flex-col gap-4 items-center shrink-0 flex-nowrap relative">
              <h1 
                className="self-stretch shrink-0 text-4xl md:text-5xl lg:text-5xl font-semibold leading-10 md:leading-[60px] lg:leading-[60px] tracking-tight relative text-center text-gray-900"
              >
                <span className="text-white">Host your </span>
                <span className="gradient-text">{(hero?.title as { highlight: string })?.highlight}</span>
                <span className="text-white"> events</span>
              </h1>
              <p className="flex max-w-[1216px] justify-center items-start shrink-0 basis-auto text-lg md:text-xl font-normal leading-normal text-white relative text-center gap-4">
                The all-in-one platform for creating memorable {hero?.description}
              </p>
            </div>
            <a 
              href="https://peatix.com/us/event/create"
              className="flex w-full max-w-[253px] py-4 px-7 gap-3 justify-center items-center shrink-0 flex-nowrap text-white rounded-lg border border-white hover:bg-white/10 transition-colors relative overflow-hidden pointer"
            >
              <span className="h-7 shrink-0 basis-auto text-lg font-semibold leading-7 relative text-left whitespace-nowrap">
                Create your event now
              </span>
            </a>
            <p className="flex justify-center items-start shrink-0 basis-auto text-lg md:text-xl font-normal leading-normal text-white relative text-center">
              No credit card required • Set up in minutes
            </p>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 